import { BrandLogo, Card, Text } from '@components/ui';
import DirectionsBoatOutlinedIcon from '@mui/icons-material/DirectionsBoatOutlined';
import DirectionsCarOutlinedIcon from '@mui/icons-material/DirectionsCarOutlined';
import RvHookupIcon from '@mui/icons-material/RvHookup';
import TwoWheelerIcon from '@mui/icons-material/TwoWheeler';
import Box from '@mui/material/Box';
import type { SvgIconProps } from '@mui/material/SvgIcon';
import type { ComponentType, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

const coverItems: Array<{
  labelKey: string;
  Icon: ComponentType<SvgIconProps>;
}> = [
  { labelKey: 'auth.covers.vehicles', Icon: DirectionsCarOutlinedIcon },
  { labelKey: 'auth.covers.motorcycles', Icon: TwoWheelerIcon },
  { labelKey: 'auth.covers.boats', Icon: DirectionsBoatOutlinedIcon },
  { labelKey: 'auth.covers.rvs', Icon: RvHookupIcon },
];

interface AuthSplitCardProps {
  children: ReactNode;
}

export const AuthSplitCard = ({
  children,
}: AuthSplitCardProps): JSX.Element => {
  const { t } = useTranslation();

  return (
    <Card
      sx={{
        position: 'relative',
        zIndex: 1,
        width: 'min(960px, calc(100% - 32px))',
        maxWidth: 960,
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: 'minmax(280px, 0.85fr) 1.15fr' },
        p: 0,
        overflow: 'hidden',
        borderRadius: 4,
        m: { xs: 2, sm: 0 },
        minHeight: { md: 850 },
      }}
    >
      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          justifyContent: 'space-between',
          gap: 4,
          p: 6,
          bgcolor: 'background.default',
        }}
      >
        <BrandLogo alt={t('common.logoAlt')} height={52} />
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 1.5,
          }}
        >
          {coverItems.map(({ labelKey, Icon }, index) => (
            <Box
              key={labelKey}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                px: 1.5,
                py: 1.75,
                borderRadius: 2,
                border: 1,
                borderColor: 'divider',
                bgcolor: 'background.paper',
              }}
            >
              <Icon
                sx={{
                  fontSize: 22,
                  color: index === 0 ? 'primary.main' : 'text.secondary',
                }}
              />
              <Text as="span" variant="caption" sx={{ fontWeight: 600 }}>
                {t(labelKey)}
              </Text>
            </Box>
          ))}
        </Box>
        <Box>
          <Text variant="display">{t('auth.tagline')}</Text>
          <Text variant="body" tone="muted" sx={{ mt: 2 }}>
            {t('auth.description')}
          </Text>
        </Box>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          px: { xs: 3, sm: 5 },
          py: { xs: 4, sm: 6 },
        }}
      >
        {children}
      </Box>
    </Card>
  );
};
